import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LabUser } from 'src/app/models/labUser.model';
import { Medic } from 'src/app/models/medic.model';
import { HttpUtilService } from '../../../services/http-util.service';
import { HttpResponse } from '@angular/common/http';
import { switchMap, tap } from 'rxjs/operators';
import { PatientPersonalInfo } from 'src/app/models/patientPersonalInfo';
import { PatientSociodemographics } from 'src/app/models/patientSociodemographics';
import { PatientLifestyle } from 'src/app/models/patientLifestyle';
import { PatientHistory } from 'src/app/models/patientHistory';
import { PatientHousehold } from 'src/app/models/patientHousehold';
import { Patient } from 'src/app/models/patient.model';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  private resourcesSubject = new BehaviorSubject<any[]>([]);
  resources$ = this.resourcesSubject.asObservable();
  private isLoaded = false;
  private cachedCountryId: string = null;
  private patientSubject = new BehaviorSubject<Patient>(null);
  public patient$ = this.patientSubject.asObservable();

  constructor(private httpUtilService: HttpUtilService) {}

  async getResources(resourcesPath: string, countryId?: string): Promise<Observable<any[]>> {
    if (this.isLoaded && (!countryId || countryId === this.cachedCountryId)) {
      // Return cached
      return this.resourcesSubject.asObservable();
    }

    let url = resourcesPath;
    if (countryId && countryId.trim() !== '' && countryId !== '0') {
      url += `?countryId=${encodeURIComponent(countryId)}`;
    }

    const response$ = await this.httpUtilService.invoke('GET', null, url, null);

    return response$.pipe(
      tap((response: HttpResponse<Object>) => {
        const data = response.body as any[];
        this.resourcesSubject.next(data);
        this.cachedCountryId = countryId || null;
        this.isLoaded = true;
      }),
      switchMap(() => this.resourcesSubject.asObservable())
    );
  }

  clearCache() {
    this.resourcesSubject.next([]);
    this.isLoaded = false;
    this.cachedCountryId = null;
  }

  setPatient(patient: Patient): void {
    this.patientSubject.next(patient);
  }

  getPatient(): Patient {
    return this.patientSubject.getValue();
  }

  updateProvinces(resourcesPath: any):Promise<Observable<HttpResponse<Object>>> {
    return this.httpUtilService.invoke('GET', null, resourcesPath, null);
  }

  updateDistricts(resourcesPath: any):Promise<Observable<HttpResponse<Object>>> {
    return this.httpUtilService.invoke('GET', null, resourcesPath, null);
  }

  save(patient: any, userId: number) {
    return this.httpUtilService.invoke('POST', patient, 'Patient', userId);
  }

  registerPatient(patient: any):Promise<Observable<HttpResponse<Object>>> {
    return this.httpUtilService.invoke('POST', patient, 'Patient/self-register', null);
  }

  createPatient(patient: any):Promise<Observable<HttpResponse<Object>>> {
    return this.httpUtilService.invoke('POST', patient, 'Patient', null);
  }

  registerMedic(medic: Medic) {
    return this.httpUtilService.invoke('POST', medic, 'users/medic', null);
  }

  registerLab(lab: LabUser) {
    return this.httpUtilService.invoke('POST', lab, 'users/lab', null);
  }

  updatePatientPersonalInfo(patientId: number, info: PatientPersonalInfo): Observable<HttpResponse<boolean>> {
    return this.httpUtilService.request('PUT', info, `Patient/${patientId}/personal-info`);
  }

  updatePatientSociodemographics(patientId: number, info: PatientSociodemographics): Observable<HttpResponse<boolean>> {
    return this.httpUtilService.request('PUT', info, `Patient/${patientId}/sociodemographics`);
  }

  updatePatientLifestyle(patientId: number, info: PatientLifestyle): Observable<HttpResponse<boolean>> {
    return this.httpUtilService.request('PUT', info, `Patient/${patientId}/lifestyle`);
  }

  updatePatientHistory(patientId: number, info: PatientHistory): Observable<HttpResponse<boolean>> {
    return this.httpUtilService.request('PUT', info, `Patient/${patientId}/history`);
  }

  updatePatientHousehold(patientId: number, info: PatientHousehold): Observable<HttpResponse<boolean>> {
    return this.httpUtilService.request('PUT', info, `Patient/${patientId}/household`);
  }

  confirmEmail(email, token) {
    const self = this;
    const apiEndpoint = 'users/confirm-email';
    const params1 = {
      key: 'email',
      value: email,
    };
    const params2 = {
      key: 'token',
      value: token,
    };
    
    return self.httpUtilService.invokeQueryWithTwoParams(
      'GET',
      params1,
      params2,
      apiEndpoint
    );
  }

  confirmAccount(email, token) {
    const self = this;
    const apiEndpoint = 'users/confirm-email';
    const params1 = {
      key: 'email',
      value: email,
    };
    const params2 = {
      key: 'token',
      value: token,
    };

    return self.httpUtilService.invokeQueryWithTwoParams(
      'GET',
      params1,
      params2,
      apiEndpoint
    );
  }

  forgotPassword(username: any) {
    const self = this;
    const apiEndpoint = 'users/forgot-password';
    const params = {
      key: 'email',
      value: username,
    }; 
    return self.httpUtilService.invokeQuery('GET', params, apiEndpoint);
  }

  sendConfirmation(username: any) {
    const self = this;
    const apiEndpoint = 'users/send-confirmation-again';
    const params = {
      key: 'email',
      value: username,
    };

    return self.httpUtilService.invokeQuery('GET', params, apiEndpoint);
  }

  resetPassword(userId: string | null, token: any, newPassword: any):Promise<Observable<HttpResponse<Object>>> {
    var resetPasswordRequest = {
      UserId: userId,
      NewPassword: newPassword,
      Token: token,
    };
    return this.httpUtilService.invoke(
      'POST',
      resetPasswordRequest,
      'users/reset-password',
      null
    );
  }

  changePassword(oldPassword: any, newPassword: any):Promise<Observable<HttpResponse<Object>>> {
    var changePasswordRequest = {
      CurrentPassword: oldPassword,
      NewPassword: newPassword,
    };

    return this.httpUtilService.invoke(
      'POST', 
      changePasswordRequest,
      'users/change-password',
      null
    );
  }

  getMedicAccessPermissions(userId: number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'patient/MedicAccessRequests';
    const params = {
      key: 'userId',
      value: userId,
    }; 
    return self.httpUtilService.invokeQuery('GET', params, apiEndpoint);
  }

  changeMedicAccessForPatient(
    userId: number,
    medicId: number,
    isMedicAuthorized: boolean,
    isFutureRequestBlocked: boolean,
    isRequestSent: boolean
  ) {
    var medicPermission = {
      user_id: userId,
      medic_id: medicId,
      is_medic_authorized: isMedicAuthorized,
      is_future_request_blocked: isFutureRequestBlocked,
      is_request_sent: isRequestSent,
    };

    return this.httpUtilService.invoke(
      'POST',
      medicPermission,
      'patient/MedicAccessRequestChange',
      null
    );
  }
  
}
